import { useState, useEffect } from 'react'
import { useWallet } from '@solana/wallet-adapter-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Wallet, User, Mail, Bell, Save, Copy, Check, X } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '../hooks/useAuth'
import { api } from '../services/api'

export default function ProfilePage() {
  const { t } = useTranslation(['profile', 'common'])
  const { connected, publicKey } = useWallet()
  const { user, isAuthenticated } = useAuth()
  const queryClient = useQueryClient()

  const [username, setUsername] = useState(user?.username || '')
  const [email, setEmail] = useState(user?.email || '')
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const [showOtpModal, setShowOtpModal] = useState(false)
  const [otpCode, setOtpCode] = useState('')
  const [pendingEmail, setPendingEmail] = useState('')

  // Update email state when user changes
  useEffect(() => {
    if (user?.email) {
      setEmail(user.email)
    }
  }, [user?.email])

  // Request email change - sends OTP
  const requestEmailChange = useMutation({
    mutationFn: async (newEmail: string) => {
      return await api.requestEmailChange(newEmail)
    },
    onSuccess: () => {
      toast.success(t('profile:toast.verificationSent'))
      setShowOtpModal(true)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || t('profile:toast.sendFailed'))
    },
  })

  // Verify email change with OTP
  const verifyEmailChange = useMutation({
    mutationFn: async ({ newEmail, code }: { newEmail: string; code: string }) => {
      return await api.verifyEmailChange(newEmail, code)
    },
    onSuccess: () => {
      toast.success(t('profile:toast.emailUpdated'))
      setShowOtpModal(false)
      setOtpCode('')
      setPendingEmail('')
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || t('profile:toast.invalidCode'))
    },
  })

  const handleSave = () => {
    // Check if email changed
    if (email !== user?.email) {
      setPendingEmail(email)
      requestEmailChange.mutate(email)
    } else {
      toast.info(t('profile:toast.noChanges'))
    }
  }

  const handleVerifyOtp = () => {
    if (otpCode.length !== 6) {
      toast.error(t('profile:toast.enterSixDigits'))
      return
    }
    verifyEmailChange.mutate({ newEmail: pendingEmail, code: otpCode })
  }

  const handleCancelOtp = () => {
    setShowOtpModal(false)
    setOtpCode('')
    setEmail(user?.email || '')
    setPendingEmail('')
  }

  const copyAddress = (address: string, fieldName: string) => {
    navigator.clipboard.writeText(address)
    setCopiedField(fieldName)
    toast.success(t('profile:toast.addressCopied'))
    setTimeout(() => setCopiedField(null), 2000)
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <Wallet className="h-16 w-16 text-gray-500 mx-auto" />
          <h2 className="text-2xl font-bold text-white">{t('profile:loginRequired.title')}</h2>
          <p className="text-gray-400">{t('profile:loginRequired.message')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen py-12">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">{t('profile:title')}</h1>
          <p className="text-gray-400">{t('profile:subtitle')}</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Stats */}
          <div className="lg:col-span-1 space-y-6">
            {/* Wallet Info Card */}
            <div className="bg-background-card rounded-xl p-6 border border-green-900/20">
              <h3 className="text-lg font-semibold text-white mb-4">{t('profile:sections.connectedWallets')}</h3>
              <div className="space-y-4">
                {/* Solana (Phantom) Wallet - for TAKARA/LAIKA */}
                <div>
                  <div className="text-sm text-gray-400 mb-2 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                    {t('profile:wallets.phantom')}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-background-elevated rounded-lg p-3 font-mono text-sm text-white break-all">
                      {connected && publicKey ? publicKey.toBase58() : user?.walletAddress || t('profile:wallets.notConnected')}
                    </div>
                    {(connected && publicKey) || user?.walletAddress ? (
                      <button
                        onClick={() => copyAddress(connected && publicKey ? publicKey.toBase58() : user?.walletAddress || '', 'solana')}
                        className="p-3 bg-background-elevated hover:bg-purple-900/20 rounded-lg transition-colors"
                      >
                        {copiedField === 'solana' ? (
                          <Check className="h-5 w-5 text-green-400" />
                        ) : (
                          <Copy className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    ) : null}
                  </div>
                </div>

                {user && (
                  <>
                    <div>
                      <div className="text-sm text-gray-400 mb-1">{t('profile:wallets.memberSince')}</div>
                      <div className="text-white font-medium">
                        {new Date(user.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </div>
                    </div>

                    <div>
                      <div className="text-sm text-gray-400 mb-1">{t('profile:wallets.lastLogin')}</div>
                      <div className="text-white font-medium">
                        {user.lastLoginAt
                          ? new Date(user.lastLoginAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })
                          : 'N/A'}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Stats Card */}
            {user && (
              <div className="bg-background-card rounded-xl p-6 border border-green-900/20">
                <h3 className="text-lg font-semibold text-white mb-4">{t('profile:sections.quickStats')}</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-400">{t('profile:stats.totalInvested')}</span>
                    <span className="text-white font-semibold">
                      ${user.totalInvested.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">{t('profile:stats.totalEarnedUSDT')}</span>
                    <span className="text-gold-500 font-semibold">
                      ${user.totalEarnedUSDT.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">{t('profile:stats.totalMinedTAKARA')}</span>
                    <span className="text-green-400 font-semibold">
                      {user.totalMinedTAKARA.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Settings */}
          <div className="lg:col-span-2 space-y-6">
            {/* Personal Information */}
            <div className="bg-background-card rounded-xl p-6 border border-green-900/20">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <User className="h-5 w-5 text-gold-500" />
                {t('profile:sections.personalInfo')}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('profile:form.username')}
                  </label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder={t('profile:form.placeholders.username')}
                    className="w-full px-4 py-3 bg-background-elevated border border-green-900/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gold-500"
                    disabled
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('profile:form.usernameReadonly')}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('profile:form.emailAddress')}
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('profile:form.placeholders.email')}
                      className="w-full pl-11 pr-4 py-3 bg-background-elevated border border-green-900/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gold-500"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('profile:form.emailHint')}
                  </p>
                </div>

                <button
                  onClick={handleSave}
                  disabled={requestEmailChange.isPending || email === user?.email}
                  className="btn-gold px-6 py-3 rounded-lg font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="h-5 w-5" />
                  {t('profile:buttons.saveChanges')}
                </button>
              </div>
            </div>

            {/* Notification Preferences */}
            <div className="bg-background-card rounded-xl p-6 border border-green-900/20">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <Bell className="h-5 w-5 text-gold-500" />
                Notification Preferences
              </h3>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-background-elevated rounded-lg">
                  <div>
                    <div className="text-white font-medium">Investment Updates</div>
                    <div className="text-sm text-gray-400">
                      Get notified when investments activate or mature
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" defaultChecked />
                    <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gold-500"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-background-elevated rounded-lg">
                  <div>
                    <div className="text-white font-medium">Claim Reminders</div>
                    <div className="text-sm text-gray-400">
                      Remind me when I have pending claims
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" defaultChecked />
                    <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gold-500"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-background-elevated rounded-lg">
                  <div>
                    <div className="text-white font-medium">Marketplace Activity</div>
                    <div className="text-sm text-gray-400">
                      Notify about Wexel sales and offers
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gold-500"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-background-elevated rounded-lg">
                  <div>
                    <div className="text-white font-medium">Platform Updates</div>
                    <div className="text-sm text-gray-400">
                      News, features, and announcements
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" defaultChecked />
                    <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gold-500"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* OTP Verification Modal */}
        {showOtpModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-background-card rounded-xl p-8 max-w-md w-full border border-green-900/20 relative">
              {/* Close button */}
              <button
                onClick={handleCancelOtp}
                className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-6 w-6" />
              </button>

              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gold-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-8 w-8 text-gold-500" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Verify Email Change</h2>
                <p className="text-gray-400">
                  We've sent a 6-digit verification code to
                </p>
                <p className="text-gold-500 font-medium mt-1">{pendingEmail}</p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder={t('profile:emailVerification.codePlaceholder')}
                    maxLength={6}
                    className="w-full px-4 py-3 bg-background-elevated border border-green-900/30 rounded-lg text-white text-center text-2xl tracking-widest font-mono placeholder-gray-500 focus:outline-none focus:border-gold-500"
                    autoFocus
                  />
                </div>

                <button
                  onClick={handleVerifyOtp}
                  disabled={verifyEmailChange.isPending || otpCode.length !== 6}
                  className="w-full btn-gold px-6 py-3 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {verifyEmailChange.isPending ? 'Verifying...' : 'Verify & Update Email'}
                </button>

                <button
                  onClick={handleCancelOtp}
                  className="w-full px-6 py-3 rounded-lg font-semibold text-gray-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>

                <div className="text-center">
                  <button
                    onClick={() => requestEmailChange.mutate(pendingEmail)}
                    disabled={requestEmailChange.isPending}
                    className="text-sm text-gold-500 hover:text-gold-400 transition-colors disabled:opacity-50"
                  >
                    {requestEmailChange.isPending ? 'Sending...' : 'Resend Code'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
